<template>
  <section>
    <div v-if="loading" class="ui-empty">
      <Spinner />
      <span>Загрузка объекта…</span>
    </div>
    <template v-if="property && !loading">
      <div v-if="hasPropertyInfo" class="property-summary ui-panel mb-5">
        <div v-if="property.city" class="flex flex-wrap items-center gap-2">
          <MapPin :size="17" class="text-primary" />
          <span class="font-medium">{{ property.city.name }}</span>
          <Badge
            v-if="property.city.utc_offset !== null && property.city.utc_offset !== undefined"
            variant="outline"
          >
            UTC{{ property.city.utc_offset >= 0 ? '+' : '' }}{{ property.city.utc_offset }}
          </Badge>
        </div>
        <Button v-if="canViewSurveys" as-child variant="outline" size="sm" aria-label="NPS-оценки">
          <router-link :to="{ name: 'surveys', query: { property_id: property.id } }">
            <ChartNoAxesCombined :size="15" />
            NPS-оценки
          </router-link>
        </Button>
        <div v-if="property.manager || property.sales_manager" class="property-managers">
          <div v-if="property.manager" class="manager-badge">
            <div class="staff-avatar">
              {{ getInitials(property.manager.first_name, property.manager.last_name) }}
            </div>
            <div>
              <div class="manager-role">Менеджер</div>
              <div class="manager-name">
                {{ property.manager.first_name }} {{ property.manager.last_name }}
              </div>
            </div>
          </div>
          <div v-if="property.sales_manager" class="manager-badge">
            <div class="staff-avatar">
              {{ getInitials(property.sales_manager.first_name, property.sales_manager.last_name) }}
            </div>
            <div>
              <div class="manager-role">Продажи</div>
              <div class="manager-name">
                {{ property.sales_manager.first_name }} {{ property.sales_manager.last_name }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="property-detail-grid">
        <div class="min-w-0">
          <div
            v-if="propertyDetailProducts.length || (!hasPropertyInfo && canViewSurveys)"
            class="section-heading products-heading"
          >
            <h2>Продукты</h2>
            <Badge v-if="propertyDetailProducts.length" variant="outline">
              {{ propertyDetailProducts.length }}
            </Badge>
            <Button
              v-if="!hasPropertyInfo && canViewSurveys"
              as-child
              variant="outline"
              size="sm"
              class="ml-auto"
              aria-label="NPS-оценки"
            >
              <router-link :to="{ name: 'surveys', query: { property_id: property.id } }">
                <ChartNoAxesCombined :size="15" />
                NPS-оценки
              </router-link>
            </Button>
          </div>
          <div v-if="propertyDetailProducts.length" class="property-products">
            <property-product
              v-for="propertyDetailProduct in propertyDetailProducts"
              :key="propertyDetailProduct.product"
              :property-detail-product="propertyDetailProduct"
              @refresh="reload()"
            />
          </div>
          <p v-else class="ui-empty">Нет установленных продуктов для данного объекта</p>
        </div>
        <aside class="property-aside">
          <slot-list
            v-if="property.slots && property.slots.length"
            :slots="property.slots"
            :queue="property.queue || []"
            @slot-changed="reload()"
          />
          <property-contacts
            v-if="property.contacts && property.contacts.length"
            :contacts="property.contacts"
            :highlighted-contact-id="highlightedContactId"
          />
        </aside>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { MapPin, ChartNoAxesCombined } from '@lucide/vue';

import { computed, ref, onMounted, watch } from 'vue';
import axios from 'axios';
import PropertyProduct from '@/components/PropertyProduct.vue';
import SlotList from '@/components/SlotList.vue';
import PropertyContacts from '@/components/PropertyContacts.vue';

interface Props {
  propertyId: number;
  highlightedContactId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  highlightedContactId: null,
});

const loading = ref(true);
const property = ref<Property | null>(null);
const propertyDetailProducts = ref<PropertyDetailProduct[]>([]);
const canViewSurveys = ref(false);
const hasPropertyInfo = computed(
  () => !!(property.value?.city || property.value?.manager || property.value?.sales_manager)
);

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last;
}

async function reload() {
  try {
    loading.value = true;
    property.value = null;
    propertyDetailProducts.value = [];

    const response = await axios.get('/api/properties/' + props.propertyId + '/');
    const data: Property = response.data;

    const versions = (data.versions ?? []).filter(isDefined);
    const supporters = (data.supporters ?? []).filter(isDefined);
    const slots = (data.slots ?? []).filter(isDefined);
    const queue = (data.queue ?? []).filter(isDefined);
    const contacts = (data.contacts ?? []).filter(isDefined);
    const modules = (data.modules ?? []).filter(isDefined);

    const propertyDetailsMap = new Map<string, PropertyDetailProduct>();

    versions.forEach((version) => {
      const supporterInfo = supporters.find((s) => s.product === version.product);
      let product = propertyDetailsMap.get(version.product);
      if (!product) {
        product = {
          product: version.product,
          productId: version.product_id,
          versions: [],
          info: supporterInfo,
          search: '',
          createdDate: version.created_at,
          propertyId: data.id,
          modules: [],
        };
        propertyDetailsMap.set(version.product, product);
      }
      product.versions.push(version);
    });

    const productWithVersion: PropertyDetailProduct[] = Array.from(
      propertyDetailsMap.values()
    ).sort((x, y) => (x.product > y.product ? 1 : -1));

    const productWithVersionIds: number[] = productWithVersion
      .map((item) => item.versions[0]?.product_id ?? -1)
      .filter((id) => id !== -1);

    const supportedProduct: PropertyDetailProduct[] = supporters
      .filter((item) => !productWithVersionIds.includes(item.product_id))
      .map((item) => ({
        propertyId: data.id,
        info: item,
        product: item.product,
        productId: item.product_id,
        createdDate: null,
        search: '',
        versions: [],
        modules: [],
      }));

    modules.forEach((module) => {
      const product = propertyDetailsMap.get(module.product_name);
      if (product) {
        product.modules.push(module);
      } else {
        const supportedProd = supportedProduct.find((p) => p.product === module.product_name);
        if (supportedProd) {
          supportedProd.modules.push(module);
        }
      }
    });

    propertyDetailProducts.value = [...productWithVersion, ...supportedProduct];
    property.value = {
      ...data,
      versions,
      supporters,
      slots,
      queue,
      contacts,
      modules,
    };
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.propertyId,
  async () => {
    await reload();
  }
);

onMounted(async () => {
  axios
    .get('/api/surveys/permissions/')
    .then((response) => {
      canViewSurveys.value = response.data.can_view === true;
    })
    .catch(() => {
      canViewSurveys.value = false;
    });
  await reload();
});

defineExpose({
  reload,
  property,
  loading,
});
</script>
